import { useMemo, useState } from "react";
import { timeZoneMap } from "./resource";
import { ChevronDown, Globe, Search, X } from "lucide-react";

const TimezoneSelector = (props) => {
  const timezones = useMemo(() => {
    return timeZoneMap.map(({ label, zone }) => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "shortOffset",
      });

      const parts = formatter.formatToParts(now);
      const offsetPart =
        parts.find((p) => p.type === "timeZoneName")?.value || "";
      const timePart = parts
        .filter((p) =>
          ["hour", "minute", "dayPeriod", "literal"].includes(p.type)
        )
        .map((p) => p.value)
        .join("");

      // Replace 'GMT+05:30' with 'UTC +05:30'
      const offset = offsetPart
        .replace("GMT", "UTC ")
        .replace("UTC+", "UTC +")
        .replace("UTC-", "UTC -");

      return {
        label: `${offset}, ${label} (${timePart})`,
        zone,
      };
    });
  }, []);

  const [selectedTimezone, setSelectedTimezone] = useState(
    props.defaultTimeZone
  );
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return timezones;

    const searchLower = searchTerm.toLowerCase();
    const searchTerms = searchLower
      .split(" ")
      .filter((term) => term.length > 0);

    return timezones
      .map(({ label, zone }) => {
        const labelLower = label.toLowerCase();
        const zoneLower = zone.toLowerCase();

        const allText = `${labelLower} ${zoneLower}`;

        // Check if all search terms are included as whole words or clear partials
        const allMatch = searchTerms.every((term) => allText.includes(term));

        if (!allMatch) return null;

        // Prioritize exact word matches or startsWith in label
        let score = 0;
        searchTerms.forEach((term) => {
          const labelWords = labelLower.split(/[\s,()/-]+/);
          if (labelWords.some((word) => word === term)) {
            score += 1000;
          } else if (labelWords.some((word) => word.startsWith(term))) {
            score += 500;
          } else if (labelLower.includes(term)) {
            score += 200;
          } else if (zoneLower.includes(term)) {
            score += 100;
          }
        });

        return { label, zone, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .map(({ label, zone }) => ({ label, zone }));
  }, [timezones, searchTerm]);

  const handleTimezoneSelect = (timezone) => {
    setSelectedTimezone(timezone);
    setShowTimezoneDropdown(false);
    setSearchTerm(""); // Clear search when selecting
    props.onSelectTimeZone(timezone);
  };

  const handleDropdownToggle = () => {
    setShowTimezoneDropdown(!showTimezoneDropdown);
    if (!showTimezoneDropdown) {
      setSearchTerm(""); // Clear search when opening
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="relative">
      <span className="absolute -top-2 left-3 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-md z-10 shadow-sm">
        Timezone
      </span>
      <button
        onClick={handleDropdownToggle}
        className="w-full flex items-center justify-between p-3 pt-5 bg-white border border-gray-300 rounded-lg hover:border-gray-400 relative"
      >
        <div className="flex items-center space-x-2">
          <Globe size={16} className="text-gray-500" />
          <span className="text-gray-900 text-sm truncate">
            {selectedTimezone.label}
          </span>
        </div>
        <ChevronDown
          className={`text-gray-400 transition-transform flex-shrink-0 ${
            showTimezoneDropdown ? "rotate-180" : ""
          }`}
          size={16}
        />
      </button>

      {showTimezoneDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowTimezoneDropdown(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-72 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search timezones..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    type="button"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Timezone List */}
            <div className="overflow-y-auto max-h-52" key={searchTerm}>
              {filteredTimezones.length > 0 ? (
                filteredTimezones.map(({ label, zone }) => (
                  <button
                    key={`${zone}-${searchTerm}`}
                    onClick={() => handleTimezoneSelect({ label, zone })}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-sm ${
                      selectedTimezone.zone === zone
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No timezones found matching "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimezoneSelector;
